import { useState, useMemo, useEffect } from 'react';
import { analyzeFileName } from '../utils/fileAnalyzer';
import { API_BASE_URL } from '../config';
import Select from 'react-select';
import useStaticData from '../hooks/useStaticData';
import SelectInfo from './SelectInfo';
import { useFetchMutation, useFetch } from '../hooks/useFetchAuth';
import { useConfirm } from "../contexts/ConfirmDialogContext";
import { checkExistingLoad, describeLoad, fileHash } from '../utils/duplicateCheck';
const CATEGORY_OPTIONS = ['BLANC', 'BRUN', 'IT', 'SSD', 'ES', 'HE', 'HA', 'AV', 'WEARABLE', 'VR', 'MISC', 'VENTES', 'STOCK', 'AUTRE'];

function FileUploadStep({ onUploadSuccess, initialData, currentUser }) {
  const { clients, shopSigns, loading, error } = useStaticData({ currentUser });
  const { mutateAsync: checkFile } = useFetchMutation();
  const { mutateAsync: uploadFile } = useFetchMutation();
  const confirm = useConfirm();
  const [file, setFile] = useState(null);
  const [fileAnalysis, setFileAnalysis] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const { data: shopSignInfo } = useFetch(
    ["mapping-descriptions", fileAnalysis?.shopSign],
    `${API_BASE_URL}/api/mapping-descriptions/${fileAnalysis?.shopSign}`,
    { enabled: !!fileAnalysis?.shopSign }
  );

  // Alert user when clients list is empty after loading
  useEffect(() => {
    if (!loading && !error && clients && clients.length === 0) {
      confirm({
        title: "Aucun client disponible",
        message: "Votre compte ne dispose d'aucun accès client. Veuillez contacter le support pour en faire la demande.",
        confirmText: "OK",
        showCancel: false,
        type: "warning",
      });
    }
  }, [loading, error, clients]);

  // Initialize with existing data if available
  useEffect(() => {
    if (initialData) {
      setFileAnalysis({
          shopSign: initialData.shopSign,
          client: initialData.client,
          year: initialData.year,
          week: initialData.week,
          category: initialData.category
      });
      if (initialData.uploadedFile) {
          setFile(initialData.uploadedFile);
      }
    }
  }, [initialData]);

  // Options for "Année" (year) from 2020 to 2030
  const yearOptions = useMemo(() => {
    return Array.from({ length: 2030 - 2019 + 1 }, (_, i) => {
      const year = 2019 + i;
      return { value: year, label: String(year) };
    });
  }, []);

  // Options for "Semaine" (week) from "01" to "52"
  const weekOptions = useMemo(() => {
    return Array.from({ length: 52 }, (_, i) => {
      const week = (i + 1).toString().padStart(2, '0');
      return { value: week, label: week };
    });
  }, []);

  const isFormValid = useMemo(() => {
    if (!fileAnalysis) return false;

    const { shopSign, client, year, week } = fileAnalysis;
    return (
      shopSign &&
      client &&
      Number(year) >= 2019 && Number(year) <= 2030 &&
      Number(week) >= 1 && Number(week) <= 52
    );
  }, [fileAnalysis]);

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];

    if (isValidFileType(droppedFile)) {
      setFile(droppedFile);
      const analysis = analyzeFileName(droppedFile.name, clients, shopSigns);
      setFileAnalysis(prev => ({
          ...prev,
          ...analysis
      }));
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];

    if (isValidFileType(selectedFile)) {
      setFile(selectedFile);
      const analysis = analyzeFileName(selectedFile.name, clients, shopSigns);

      setFileAnalysis(prev => ({
          ...prev,
          ...analysis
      }));
    }
  };

  const isValidFileType = (file) => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    return file && validTypes.includes(file.type);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Throws when the check itself fails: the caller must not upload without an answer
  const checkExistingFile = async () => {
    setIsChecking(true);
    try {
      return await checkExistingLoad(checkFile, API_BASE_URL, fileAnalysis, {
        filename: file.name,
        fileHash: await fileHash(file),
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleUploadClick = async () => {
    if (!isFormValid || !file) return;

    let check;
    try {
      check = await checkExistingFile();
    } catch (error) {
      console.error('Check existing file error:', error);
      await confirm({
        title: "Vérification impossible",
        message: "Impossible de vérifier si ce fichier a déjà été traité. Le chargement est bloqué : réessayez dans un instant.",
        confirmText: "OK",
        cancelText: "Annuler",
        showCancel: false,
        type: "error"
      });
      return;
    }

    const week = `${fileAnalysis?.shopSign} / ${fileAnalysis?.client} / ${fileAnalysis?.category || 'default'}, S${fileAnalysis?.week} ${fileAnalysis?.year}`;

    // The same file (same name or same content) in this week: the server refuses it too
    if (check.exists) {
      await confirm({
        title: "Fichier déjà traité",
        message: (
          <div>
            <p>Ce fichier a déjà été traité pour {week} :</p>
            <ul className="mt-2 ml-4 list-disc space-y-0.5">
              {check.sameFile.map((load) => (
                <li key={load.filename}>
                  {describeLoad(load)}
                  {load.same_content && !load.same_name && ' : même contenu sous un autre nom'}
                </li>
              ))}
            </ul>
            <p className="mt-3">Le traitement du même fichier deux fois n'est pas autorisé : supprimez l'ancien chargement dans l'Historique ou utilisez Retraiter.</p>
          </div>
        ),
        confirmText: "OK",
        showCancel: false,
        type: "error"
      });
      return;
    }

    // Another file of this week, or this file under another univers: allowed after a warning
    if (check.otherFiles.length > 0 || check.elsewhere.length > 0) {
      const proceed = await confirm({
        // This very file loaded elsewhere matters more than other files of the week
        title: check.elsewhere.length > 0 ? "Fichier déjà chargé" : "Semaine déjà chargée",
        message: (
          <div className="space-y-3">
            {check.otherFiles.length > 0 && (
              <div>
                <p>Cette semaine ({week}) contient déjà :</p>
                <ul className="mt-1 ml-4 list-disc space-y-0.5">
                  {check.otherFiles.map((load) => <li key={load.filename}>{describeLoad(load)}</li>)}
                </ul>
                <p className="mt-2">Vérifiez que les deux fichiers ne contiennent pas les mêmes ventes : elles seraient comptées deux fois.</p>
              </div>
            )}
            {check.elsewhere.length > 0 && (
              <div>
                <p>Ce fichier est déjà chargé cette semaine sous :</p>
                <ul className="mt-1 ml-4 list-disc space-y-0.5">
                  {check.elsewhere.map((load) => (
                    <li key={`${load.shop_sign}/${load.client}/${load.category}/${load.filename}`}>
                      {load.shop_sign} / {load.client} / {load.category} : {describeLoad(load)}, {load.same_name ? 'même nom' : 'même contenu'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ),
        confirmText: "Charger quand même",
        cancelText: "Annuler",
        type: "warning"
      });
      if (!proceed) return;
    }

    proceedWithUpload();
  };

  const proceedWithUpload = async () => {
    if (!isFormValid || !file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('shop_sign', fileAnalysis.shopSign);
      formData.append('client', fileAnalysis.client);
      formData.append('year', fileAnalysis.year);
      formData.append('week', fileAnalysis.week);
      formData.append('category', fileAnalysis.category);

      const result = await uploadFile({
        url: `${API_BASE_URL}/api/upload/`,
        method: 'POST',
        body: formData
      });

      onUploadSuccess({
        ...result,
        shopSign: fileAnalysis.shopSign,
        client: fileAnalysis.client,
        year: fileAnalysis.year,
        week: fileAnalysis.week,
        category: fileAnalysis.category,
        file: file,
      });
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center dark:text-white">Chargement des données...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && <div className="text-red-500">Error: {error}</div>}

      <div
        className="text-center p-4 border-2 border-dashed border-gray-700/10 rounded-lg bg-blue-500/10"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="fileInput"
          className="hidden"
          accept=".xls,.xlsx"
          onChange={handleFileSelect}
        />
        <label
          htmlFor="fileInput"
          className="cursor-pointer inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          {file ? 'Charger un autre fichier' : 'Choisir un fichier'}
        </label>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          ou glissez-déposez votre fichier ici
        </p>
        <p className="text-sm text-gray-500 mt-1">
          (formats acceptés: .xls, .xlsx)
        </p>
      </div>

        {(file || fileAnalysis) && (
          <div className="mt-6 space-y-6">
              {file && (
                <div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-800 dark:text-white">
                    <h3 className="font-semibold text-gray-800 dark:text-white">Fichier sélectionné:</h3>
                    <p className="text-gray-600 dark:text-white">Nom: {file.name}</p>
                    <p className="text-gray-600 dark:text-white">Taille: {formatFileSize(file.size)}</p>
                </div>
              )}

              {fileAnalysis && (
                <>
                  <div className="p-4 bg-blue-50 rounded-lg dark:bg-gray-800 dark:text-white">
                    <h3 className="font-semibold text-gray-800 mb-3 dark:text-white">Metadata du fichier</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="relative">
                        <Select
                          options={shopSigns.map(sign => ({ value: sign, label: sign })).sort((a, b) => a.label.localeCompare(b.label))}
                            value={fileAnalysis.shopSign ? { value: fileAnalysis.shopSign, label: fileAnalysis.shopSign } : null}
                            onChange={(option) => setFileAnalysis({ ...fileAnalysis, shopSign: option.value })}
                            className="mt-1"
                            classNamePrefix="react-select"
                            placeholder="Enseigne "
                        />
                        <SelectInfo description="Sélectionnez l'enseigne des données Sellout que vous avez chargées" />
                        </div>
                      </div>
                      <div>
                        <div className="relative">
                        <Select
                          options={clients.map(client => ({ value: client, label: client })).sort((a, b) => a.label.localeCompare(b.label))}
                          value={fileAnalysis.client ? { value: fileAnalysis.client, label: fileAnalysis.client } : null}
                          onChange={(option) => setFileAnalysis({ ...fileAnalysis, client: option.value })}
                          className="mt-1"
                          classNamePrefix="react-select"
                          placeholder="Client"
                        />
                        <SelectInfo description="Sélectionnez le client des données Sellout que vous avez chargées" />
                        </div>
                      </div>
                      <div>
                        <div className="relative">
                        <Select
                          options={CATEGORY_OPTIONS.map(category => ({ value: category, label: category }))}
                          value={fileAnalysis.category ? { value: fileAnalysis.category, label: fileAnalysis.category } : null}
                          onChange={(option) => setFileAnalysis({ ...fileAnalysis, category: option.value })}
                          className="mt-1"
                          classNamePrefix="react-select"
                          placeholder="Univers (facultatif)"
                        />
                        <SelectInfo description="Sélectionnez l'univers des données Sellout que vous avez chargées (facultatif)" />
                        </div>
                      </div>
                      <div>
                        <div className="relative">
                        <Select
                          options={yearOptions}
                          value={fileAnalysis.year ? { value: fileAnalysis.year, label: String(fileAnalysis.year) } : null}
                          onChange={(option) => setFileAnalysis({ ...fileAnalysis, year: option.value })}
                          className="mt-1"
                          classNamePrefix="react-select"
                          placeholder="Année"
                        />
                        <SelectInfo description="Sélectionnez l'année des données Sellout que vous avez chargées" />
                        </div>
                      </div>
                      <div>
                        <div className="relative">
                        <Select
                          options={weekOptions}
                          value={fileAnalysis.week ? { value: fileAnalysis.week, label: fileAnalysis.week } : null}
                          onChange={(option) => setFileAnalysis({ ...fileAnalysis, week: option.value })}
                          className="mt-1"
                          classNamePrefix="react-select"
                          placeholder="Semaine"
                          />
                          <SelectInfo description="Sélectionnez la semaine des données Sellout que vous avez chargées" />
                        </div>
                      </div>
                    </div>
                  </div>
                  {shopSignInfo?.descriptions?.length > 0 && (
                    <div className="px-4 py-3 rounded-lg  flex items-baseline gap-5">
                      <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2">
                        Traitement spécial {fileAnalysis.shopSign}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {shopSignInfo.descriptions.map((label, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end">
                    <button
                      onClick={handleUploadClick}
                      disabled={!isFormValid || isUploading || isChecking || !file}
                      className={`px-6 py-2 rounded-lg text-white font-semibold
                        ${isFormValid && !isUploading && !isChecking && file
                            ? 'bg-blue-600 hover:bg-blue-700'
                            : 'bg-gray-400 cursor-not-allowed'}
                        transition-colors duration-200 flex items-center justify-center`}
                    >
                      {isUploading || isChecking ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {isChecking ? 'Vérification...' : 'Chargement...'}
                        </>
                        ) : (
                          'Suivant'
                        )}
                    </button>
                  </div>
                </>
              )}
          </div>
        )}
    </div>
  );
}

export default FileUploadStep;